import { useEffect, useRef, useState } from 'react';
import {
  AutoComplete,
  AutoCompleteItem,
  Box,
  Switch,
  Typography,
  getTokenValue,
} from '@drivekyte/ui';
import mapAutocompleteData from '@/components/search-bar/components/input-address/utils/mapAutocompleteData';
import SearchEmptyState from '@/components/search-empty-state';
import RecentSearches from '@/components/recent-searches';
import useOutsideServiceAreaExperiment from '@/hooks/experiments/use-outside-service-area-experiment/use-outside-service-area-experiment';
import useAnalyticsWithBookingCart from '@/hooks/use-analytics-with-booking-cart-data';
import { ServiceAreaLabel } from '@/hooks/use-service-area-labels';
import useRentalSearchHistory from '@/hooks/use-rental-search-history';
import useEnableRecentSearches from '@/hooks/use-enable-recent-searches';
import usePlacesSearch, { Place } from '@/hooks/use-places-search-wrapper';
import SearchInput from '../../../search-input';
import useVagueToLotRedirect from '@/hooks/use-vague-to-lot-redirect';
import useAddressInputState from '@/hooks/use-address-input-state';

type WhereInputProps = {
  id: string;
  label: string;
  placeholder?: string;
  onFocus?: () => void;
  onBlur?: () => void;
  onSelect: (address: Place) => void;
  initialInputValue: string;
  sameAddressToggle: {
    value: boolean;
    onToggle: (value: boolean) => void;
    label: string;
  };
  testID?: string;
  isStartLeg?: boolean;
  isEndLeg?: boolean;
};

const WhereInput = ({
  id,
  label,
  placeholder,
  initialInputValue,
  onSelect,
  testID,
  sameAddressToggle,
  isStartLeg,
  isEndLeg,
}: WhereInputProps) => {
  const {
    addressInputValue: searchInputTextValue,
    setAdressInputValue: setSearchInputTextValue,
  } = useAddressInputState((state) => state[id]);

  const [isInputValueSelected, setIsInputValueSelected] =
    useState<boolean>(false);
  const [selectedServiceArea, setSelectedServiceArea] = useState<string | null>(
    null,
  );
  const [outsideServiceArea, setOutsideServiceArea] = useState<boolean>(false);
  const [isFocused, setIsFocused] = useState<boolean>(false);
  // TODO: Remove this when the experiment has concluded: https://app.posthog.com/experiments/4986
  const { isOutsideServiceAreaExperimentTreatment } =
    useOutsideServiceAreaExperiment();

  const isNotInitialValue = searchInputTextValue !== initialInputValue;
  const placesSearchValue =
    isNotInitialValue && !outsideServiceArea ? searchInputTextValue : '';
  const { isLoading, isIdle, data } = usePlacesSearch(placesSearchValue);

  const { data: enableRecentSearches } = useEnableRecentSearches();
  const { enableAddressToggler } = useVagueToLotRedirect();

  const { trackEvent, AnalyticsEvents } = useAnalyticsWithBookingCart();
  const { fetchSearchHistory } = useRentalSearchHistory();
  const { totalHistoryItems } = fetchSearchHistory();

  useEffect(() => {
    //Force value update if the address value is changed by the parent component
    if (initialInputValue !== '') {
      setSearchInputTextValue(initialInputValue);
    }
  }, [initialInputValue]);

  useEffect(() => {
    if (searchInputTextValue.length) return;
    setIsInputValueSelected(false);
  }, [searchInputTextValue]);

  const hasData = !isLoading && data && data.length > 0;

  useEffect(() => {
    if (hasData && !!selectedServiceArea) {
      setSelectedServiceArea(null);
      const serviceAreaPlace = data.find(
        (place) => place.place_type === 'city',
      );
      if (serviceAreaPlace) onSelect(serviceAreaPlace);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasData, selectedServiceArea]);

  const handleSelect = (autoCompleteItem: AutoCompleteItem<Place> | null) => {
    trackEvent(AnalyticsEvents.Search.SuggestionClicked, {
      search_input: searchInputTextValue,
      suggestion_text: autoCompleteItem?.value,
      address: autoCompleteItem?.extraData?.formatted_address,
      element_id: testID,
      service_area: autoCompleteItem?.extraData?.service_area_code,
      is_covered_location: autoCompleteItem?.extraData?.is_covered_location,
      place_type: autoCompleteItem?.extraData?.place_type,
      initial_input: initialInputValue,
      place_lat: autoCompleteItem?.extraData?.lat,
      place_lng: autoCompleteItem?.extraData?.lng,
      outside_service_area_exp: isOutsideServiceAreaExperimentTreatment,
    });
    if (autoCompleteItem?.extraData?.is_covered_location === false) {
      setSearchInputTextValue(autoCompleteItem.value);
      setOutsideServiceArea(true);
      return;
    }
    if (autoCompleteItem?.value && autoCompleteItem?.extraData) {
      setSearchInputTextValue(autoCompleteItem.value);
      onSelect(autoCompleteItem.extraData);
    }
    setIsFocused(true);
    setIsInputValueSelected(true);
  };

  const handleSelectedServiceArea = (item: ServiceAreaLabel) => {
    setOutsideServiceArea(false);
    setSelectedServiceArea(item.code);
    trackEvent(AnalyticsEvents.Search.CityTagSelected, {
      serviceArea: item.code,
      testID,
      searchValue: searchInputTextValue,
      is_edit_trip: false,
      outside_service_area_exp: isOutsideServiceAreaExperimentTreatment,
    });
    setSearchInputTextValue(item.cityAddress);
  };

  const handleInputChange = (newInputValue: string) => {
    if (newInputValue !== searchInputTextValue) {
      if (outsideServiceArea) {
        setOutsideServiceArea(false);
      }
      if (selectedServiceArea) {
        setSelectedServiceArea(null);
      }
      setSearchInputTextValue(newInputValue);
    }
  };

  const autoCompleteData = mapAutocompleteData(
    data?.filter(
      (place) =>
        place.is_covered_location || isOutsideServiceAreaExperimentTreatment,
    ) ?? [],
  );

  const inputRef = useRef<HTMLInputElement>(null);

  const focusInput = () => {
    inputRef.current?.focus();
  };

  const showAutoCompleteDropdown =
    isFocused && isNotInitialValue && (!isIdle || outsideServiceArea);

  const showAutoCompleteRecentSearches = !!totalHistoryItems
    ? isFocused &&
      !isInputValueSelected &&
      !showAutoCompleteDropdown &&
      !!enableRecentSearches?.enabled
    : false;

  const showAutoComplete =
    showAutoCompleteRecentSearches || showAutoCompleteDropdown;

  const handleOnClose = () => setIsFocused(false);
  const handleOnFocus = () => {
    if (!!initialInputValue) {
      inputRef.current?.select();
    }
    setIsFocused(true);
  };
  const handleOnBlur = () => {
    if (!showAutoComplete) setIsFocused(false);
  };
  const handleOuterPress = () => {
    if (isFocused) {
      setIsFocused(false);
    }
  };

  useEffect(() => {
    if (placesSearchValue && autoCompleteData.length) {
      trackEvent(AnalyticsEvents.BookingFlow.SearchAddressType, {
        address: placesSearchValue,
        is_start_leg: !!isStartLeg,
        is_end_leg: !!isEndLeg,
      });
    }
  }, [JSON.stringify(autoCompleteData)]);

  return (
    <Box width={260}>
      <AutoComplete<Place>
        open={showAutoComplete}
        inputValue={searchInputTextValue}
        loading={isLoading}
        onInputValueChange={handleInputChange}
        onOuterPress={handleOuterPress}
        testID={testID}
        data={autoCompleteData}
        onSelect={handleSelect}
        customEmptyState={
          <Box padding="base" paddingHorizontal="$medium">
            {showAutoCompleteRecentSearches ? (
              <RecentSearches onClose={handleOnClose} />
            ) : (
              <SearchEmptyState
                onServiceAreaItemPress={handleSelectedServiceArea}
                hideServiceAreas={false}
                outsideServiceArea={outsideServiceArea}
              />
            )}
          </Box>
        }
        renderInput={(inputOptions) => (
          <SearchInput
            inputOptions={inputOptions}
            isFocused={isFocused}
            label={label}
            onBlur={handleOnBlur}
            onFocus={handleOnFocus}
            onPressLabel={focusInput}
            placeholder={placeholder}
            ref={inputRef}
          />
        )}
        containerStyle={{
          borderRadius: '$xxLarge',
          width: 420,
          backgroundColor: '$backgroundPrimary',
          shadowColor: 'rgba(0, 0, 0, 0.14)',
          shadowOffset: { width: 0, height: 1 },
          shadowRadius: 8,
          left: 0,
          top: 72,
          paddingVertical: showAutoComplete ? '$medium' : 0,
          paddingTop: showAutoComplete ? '$large' : 0,
        }}
        itemStyle={{
          paddingRight: `${getTokenValue('$medium', 'space')}px`,
          paddingLeft: `${getTokenValue('$medium', 'space')}px`,
        }}
        loadingContainerStyle={{
          paddingHorizontal: '$medium',
        }}
        listFooterComponent={() => (
          <Box
            marginTop="$medium"
            display="flex"
            flexDirection="row"
            paddingHorizontal="$medium"
          >
            <Switch
              value={sameAddressToggle.value}
              onChange={sameAddressToggle.onToggle}
              size="small"
              disabled={!enableAddressToggler}
            />
            <Box marginLeft="$xSmall">
              <Typography variant="paragraphSmall">
                {sameAddressToggle.label}
              </Typography>
            </Box>
          </Box>
        )}
      />
    </Box>
  );
};

export default WhereInput;
